import { useCallback, useEffect, useRef } from "react";

import { Minus } from "@/components/icons/minus";
import { Plus } from "@/components/icons/plus";

type LogoResizeProps = {
  logoSize: number;
  minLogoSize: number;
  maxLogoSize: number;
  onLogoSizeChange: (size: number) => void;
  label?: string;
};

export default function LogoResize({
  logoSize,
  minLogoSize = 10,
  maxLogoSize = 100,
  onLogoSizeChange,
  label,
}: LogoResizeProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = logoSize.toString();
    }
  }, [logoSize]);

  const minMax = useCallback(
    (value: number) => Math.min(Math.max(value, minLogoSize), maxLogoSize),
    [minLogoSize, maxLogoSize]
  );

  const handleLogoSize = useCallback(
    (direction: number) => () => {
      const newSize = minMax(Number(inputRef.current?.value) + direction);
      inputRef.current.value = newSize.toString();
      onLogoSizeChange(newSize);
    },
    [minMax, onLogoSizeChange]
  );

  const updateLogoSize = useCallback(
    (newLogoSize: number) => {
      const newSize = minMax(newLogoSize);
      inputRef.current.value = newSize.toString();
      onLogoSizeChange(newSize);
    },
    [minMax, onLogoSizeChange]
  );

  return (
    <div className="grid h-12 w-full grid-cols-[3rem_1fr_3rem] rounded border border-solid border-gray-300">
      <button
        className="flex items-center justify-center"
        onClick={handleLogoSize(-10)}
      >
        <Minus className="size-4" />
      </button>
      <input
        ref={inputRef}
        className="min-w-0 border-x border-solid border-gray-300 text-center outline-none"
        type="text"
        defaultValue={logoSize}
        aria-label={label}
        onBlur={(e) => updateLogoSize(Number(e.target.value))}
        onKeyDown={(e) =>
          e.key === "Enter" &&
          updateLogoSize(Number((e.target as HTMLInputElement).value))
        }
      />
      <button
        className="flex items-center justify-center"
        onClick={handleLogoSize(10)}
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}
